import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { CartItem } from 'src/models/cart/_types';
import { cartTypeToItemType } from 'src/models/cart/utils';
import { SearchAndAddItem } from 'src/pages/Clients/PaymentDialog/SearchAndAddItem';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { WithSearchResult } from 'src/types/utilityTypes';

interface PosConfigItemBrowserProps {
  selectedItemType: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  debouncedSearchValue: string;
  onSelect: (item: WithSearchResult<any>) => void;
  onRemove: (item: CartItem, index?: number) => void;
  cartItems: CartItem[];
}

// Simple "All" options for group and plan types
const getSimpleAllOption = (type: string) => {
  const options: Record<string, any[]> = {
    group: [
      {
        id: 'all-groups',
        name: 'All Groups',
        type: 'group',
        itemId: 'all-groups',
        itemType: 'group',
        description: 'Browse all available groups',
        paymentOptions: [],
      },
    ],
    plan: [
      {
        id: 'all-plans',
        name: 'All Plans',
        type: 'plan',
        itemId: 'all-plans',
        itemType: 'plan',
        description: 'Browse all available plans',
        paymentOptions: [],
      },
    ],
  };

  return options[type] || [];
};

export const PosConfigItemBrowser: React.FC<PosConfigItemBrowserProps> = ({
  selectedItemType,
  searchValue,
  onSearchChange,
  debouncedSearchValue,
  onSelect,
  onRemove,
  cartItems,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'posConfig' });
  const { session } = useSession();

  // Compute items directly from session data
  const items = useMemo(() => {
    if (!selectedItemType) return [];

    if (selectedItemType === 'package') {
      // Use live package tags from session
      const packageTags = session?.packageTags || [];

      return [
        {
          id: 'all-packages',
          name: 'All Packages',
          type: 'package',
          itemId: 'all-packages',
          itemType: 'package',
          description: 'Browse all available packages',
          paymentOptions: [],
        },
        // Convert package tags to items
        ...packageTags.map((tag, index) => ({
          id: `package-tag-${index}`,
          name: tag,
          type: 'package',
          itemId: `package-tag-${index}`,
          itemType: 'package',
          description: `Packages tagged with "${tag}"`,
          paymentOptions: [],
          labelId: tag, // Store the tag for filtering
        })),
      ];
    } else if (selectedItemType === 'product') {
      // Use live product tags from session
      const productTags = session?.productTags || [];

      return [
        {
          id: 'all-products',
          name: 'All Products',
          type: 'product',
          itemId: 'all-products',
          itemType: 'product',
          description: 'Browse all available products',
          paymentOptions: [],
        },
        // Convert product tags to items
        ...productTags.map((tag, index) => ({
          id: `product-tag-${index}`,
          name: tag,
          type: 'product',
          itemId: `product-tag-${index}`,
          itemType: 'product',
          description: `Products tagged with "${tag}"`,
          paymentOptions: [],
          labelId: tag, // Store the tag for filtering
        })),
      ];
    } else {
      // Use simple "All" options for groups and plans
      return getSimpleAllOption(selectedItemType);
    }
  }, [selectedItemType, session?.packageTags, session?.productTags]);

  // Filter items based on search
  const filteredItems = items.filter(
    (item) =>
      item.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()) ||
      item.description
        ?.toLowerCase()
        .includes(debouncedSearchValue.toLowerCase())
  );

  // Find the selected ItemType object from the string value
  const selectedItemTypeObj =
    selectableItemTypes.find((type) => type.value === selectedItemType) || null;

  const resetFilters = () => {
    onSearchChange('');
  };

  if (!items.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('noItemsFound', { type: selectedItemType })}
        </Typography>
        <Typography variant="body2">
          {t('itemsWillAppear', { type: selectedItemType })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SearchAndAddItem
        onAdd={(item) => onSelect(item as WithSearchResult<any>)}
        onRemove={onRemove}
        itemList={cartItems}
        isLoading={false}
        searchResults={filteredItems}
        activeFilters={!!debouncedSearchValue}
        resetFilters={resetFilters}
        selectedItemType={selectedItemTypeObj}
        cartItems={cartItems}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        checkIfItemAdded={(item) => {
          return !!cartItems.find((i) => {
            return (
              i.itemId === item.id &&
              cartTypeToItemType(i.itemType) === item.type
            );
          });
        }}
      />
    </Box>
  );
};
